import { api } from '@spartalabs/pdc-core';
import {
  isMfaPendingResponse,
  MfaPendingResponse,
  MfaSetupResponse,
  MfaVerifyPayload,
} from './mfaTypes';
import { mapLoginResponseToUser } from './portalAuthMapper';

const removesSpecialCharacters = (value: string) => value.replace(/\D/g, '');

export const MFA_REQUIRED_STATUS = 'MFA_REQUIRED';

export type MfaRequiredQueryError = {
  status: typeof MFA_REQUIRED_STATUS;
  data: MfaPendingResponse;
};

export const isMfaRequiredQueryError = (
  error: unknown,
): error is MfaRequiredQueryError =>
  typeof error === 'object' &&
  error !== null &&
  (error as MfaRequiredQueryError).status === MFA_REQUIRED_STATUS &&
  isMfaPendingResponse((error as MfaRequiredQueryError).data);

/**
 * Overrides pdc-core auth transforms so PDV funcionalidade codigos (e.g.
 * faturamento_acesso) map to portal Permission keys expected by the UI.
 */
const authApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: builder => ({
    login: builder.mutation({
      async queryFn({ username, password }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: 'api/LoginPdc',
          method: 'POST',
          data: {
            usuario: removesSpecialCharacters(username),
            senha: password,
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        if (isMfaPendingResponse(result.data)) {
          return {
            error: {
              status: MFA_REQUIRED_STATUS,
              data: result.data,
            },
          };
        }

        return {
          data: mapLoginResponseToUser(
            result.data as Parameters<typeof mapLoginResponseToUser>[0],
          ),
        };
      },
    }),
    resetPassword: builder.mutation({
      async queryFn({ username, pwd, newPwd }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: 'api/LoginPdc/AlterarSenha',
          method: 'POST',
          data: {
            cpf: removesSpecialCharacters(username),
            senha: pwd,
            novaSenha: newPwd,
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        if (isMfaPendingResponse(result.data)) {
          return {
            error: {
              status: MFA_REQUIRED_STATUS,
              data: result.data,
            },
          };
        }

        return {
          data: mapLoginResponseToUser(
            result.data as Parameters<typeof mapLoginResponseToUser>[0],
          ),
        };
      },
    }),
    forgotPassword: builder.mutation({
      query: (data: { cpf: string; email: string; birthdate: string }) => ({
        url: 'api/LoginPdc/EsqueciSenha',
        method: 'post',
        data: {
          cpf: removesSpecialCharacters(data.cpf),
          email: data.email.trim(),
          dataNascimento: data.birthdate,
        },
      }),
    }),
    mfaSetup: builder.mutation<MfaSetupResponse, { mfaToken: string }>({
      async queryFn({ mfaToken }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: 'api/LoginPdc/Mfa/Setup',
          method: 'POST',
          headers: { Authorization: `Bearer ${mfaToken}` } as never,
        });

        if (result.error) {
          return { error: result.error };
        }

        const response = result.data as MfaSetupResponse;
        return {
          data: {
            setupKey: response.setupKey,
            manualEntryKey: response.manualEntryKey,
            qrCodeImageUrl: response.qrCodeImageUrl,
          },
        };
      },
    }),
    mfaVerify: builder.mutation<User, MfaVerifyPayload>({
      async queryFn({ mfaToken, code, setupKey }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: 'api/LoginPdc/Mfa/Verify',
          method: 'POST',
          headers: { Authorization: `Bearer ${mfaToken}` } as never,
          data: {
            code,
            setupKey,
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: mapLoginResponseToUser(
            result.data as Parameters<typeof mapLoginResponseToUser>[0],
          ),
        };
      },
    }),
  }),
});

export const {
  useMfaSetupMutation,
  useMfaVerifyMutation,
} = authApi;
